import { ContactForm } from '../../components/forms/ContactForm';
import { DemoPopup } from '../../components/forms/DemoPopup';
import { Button } from '../../components/ui/button';
import {
  PieChart,
  BookOpen,
  Wallet,
  Receipt,
  FileText,
  CheckSquare,
  CheckCircle2,
} from 'lucide-react';

const features = [
  {
    icon: BookOpen,
    title: 'Accounting',
    description: 'Double-entry bookkeeping, chart of accounts, and automated journal entries.',
  },
  {
    icon: Wallet,
    title: 'Budgeting',
    description: 'Create, track, and manage budgets across all departments.',
  },
  {
    icon: Receipt,
    title: 'Expense Tracking',
    description: 'Track expenses, receipts, and reimbursements efficiently.',
  },
  {
    icon: FileText,
    title: 'Invoicing',
    description: 'Professional invoices, payment tracking, and automated reminders.',
  },
  {
    icon: PieChart,
    title: 'Financial Reports',
    description: 'P&L, balance sheets, cash flow, and customizable reports.',
  },
  {
    icon: CheckSquare,
    title: 'Compliance',
    description: 'Tax compliance, audit trails, and regulatory reporting.',
  },
];

const benefits = [
  'Real-time financial visibility',
  'Reduce accounting errors by 90%',
  'Faster month-end closing',
  'Better cash flow management',
  'Audit-ready documentation',
  'Multi-currency support',
];

export const FinancePage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="finance-page">
      {/* Hero Banner */}
      <section className="py-20 md:py-28 hero-glow" data-testid="finance-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in-up">
              Finance Management
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white animate-fade-in-up stagger-1">
              Take Control of Your Finances
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mt-6 animate-fade-in-up stagger-2">
              Comprehensive financial management with accounting, budgeting, and real-time insights.
            </p>
            <div className="mt-8 animate-fade-in-up stagger-3">
              <DemoPopup
                title="Request Demo for Finance Management"
                formType="Finance Management Demo"
                defaultSolution="Finance Management"
                trigger={
                  <Button
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium glow-primary glow-primary-hover btn-shine"
                    data-testid="finance-hero-demo-btn"
                  >
                    Request Demo
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 md:py-32" data-testid="finance-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
              Key Features
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Everything you need for complete financial control
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`glass rounded-2xl p-8 card-hover animate-fade-in-up stagger-${index + 1}`}
                data-testid={`finance-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="feature-icon mb-6">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold font-heading text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 bg-secondary/30" data-testid="finance-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Master Your Financial Operations
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our Finance Management solution gives you complete control and visibility over your finances.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="animate-fade-in-up stagger-2">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1618044733300-9472054094ee?w=800&q=80"
                  alt="Finance Dashboard"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Form Section */}
      <section className="py-24 md:py-32" data-testid="finance-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                Request a Demo for Finance Management
              </h2>
              <p className="text-muted-foreground text-lg mt-4">
                See how we can streamline your financial operations.
              </p>
            </div>

            <div className="glass rounded-2xl p-8 animate-fade-in-up stagger-1" data-testid="finance-demo-form-wrapper">
              <ContactForm
                formType="Finance Management Demo"
                showInterestedIn={false}
                showCompanySize={true}
                interestedInDefault="Finance Management"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancePage;
